<template>
  <div class="lg-chart" :style="{ height: `${height}px` }">
    <!-- Loading -->
    <div v-if="loading" class="lg-chart-state" aria-live="polite">
      <Loader2 class="lg-chart-spin" :size="20" aria-hidden="true" />
      <span>Loading…</span>
    </div>

    <!-- Empty / no data -->
    <div v-else-if="!hasData" class="lg-chart-state">
      <GoblinIcon variant="empty" :size="40" />
      <span>{{ emptyLabel }}</span>
    </div>

    <!-- Render error (canvas unavailable, bad data) -->
    <div v-else-if="renderError" class="lg-chart-state error">
      <GoblinIcon variant="error" :size="40" />
      <span>{{ renderError }}</span>
    </div>

    <!-- uPlot mounts here -->
    <div v-show="hasData && !renderError" ref="host" class="lg-chart-host" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import GoblinIcon from '../components/GoblinIcon.vue';
import { formatMetric, formatClockTime, type MetricUnit } from '../lib/format-metric.js';
import { resolveVizPalette, resolveCssVar, colorForName } from '../lib/viz-palette.js';

export type VizKind = 'line' | 'bar' | 'area' | 'points';

export interface ChartSeries {
  /** Legend label + deterministic color key. */
  name: string;
  /** `[timestampMs, value]` pairs. Need not share x with other series. */
  points: Array<[number, number]>;
  /** Explicit CSS color; else assigned from the token palette by name. */
  color?: string;
  /** Per-series render override; falls back to the chart-level `type`. */
  kind?: VizKind;
}

const props = withDefaults(
  defineProps<{
    series: ChartSeries[];
    /** Chart-level default render kind (per-series `kind` overrides it). */
    type?: VizKind;
    /** Y-axis unit → value formatter for axis ticks and the cursor legend. */
    yUnit?: MetricUnit;
    yScale?: 'linear' | 'log';
    /** Format the x-axis as time (values are unix ms). */
    xIsTime?: boolean;
    height?: number;
    /**
     * Update data in place (u.setData) when only the points change, instead of
     * tearing down and re-initialising uPlot — for live-tailing charts.
     */
    live?: boolean;
    loading?: boolean;
    emptyLabel?: string;
  }>(),
  {
    type: 'line',
    yUnit: 'none',
    yScale: 'linear',
    xIsTime: true,
    height: 240,
    live: false,
    loading: false,
    emptyLabel: 'No data in this range',
  },
);

const host = ref<HTMLElement | null>(null);
const renderError = ref<string | null>(null);
let chart: uPlot | null = null;
let ro: ResizeObserver | null = null;
let rafId = 0;

const hasData = computed(
  () => props.series.length > 0 && props.series.some((s) => s.points.length > 0),
);

/**
 * Structural signature — when any of this changes uPlot must be rebuilt (series
 * identity, render kind, axis/scale config). Point *values* are excluded so a
 * live data tick can take the cheap `setData` path.
 */
const structure = computed(() =>
  JSON.stringify({
    names: props.series.map((s) => s.name),
    kinds: props.series.map((s) => s.kind ?? props.type),
    colors: props.series.map((s) => s.color ?? null),
    type: props.type,
    yUnit: props.yUnit,
    yScale: props.yScale,
    xIsTime: props.xIsTime,
  }),
);

/**
 * Cheap revision signal for the series *data* — series count plus each
 * series' point count and last `[ts, value]`, instead of deep-watching every
 * point on every tick (`deep: true` is O(total points) and re-traverses the
 * whole growing series each tick, defeating the point of `live`). A full
 * series replacement still changes this in the common append-only live-tail
 * case (different length and/or last point).
 */
const seriesRevision = computed(() =>
  props.series
    .map((s) => {
      const last = s.points[s.points.length - 1];
      return `${s.name}:${s.points.length}:${last ? `${last[0]},${last[1]}` : ''}`;
    })
    .join('|'),
);

/** Build uPlot's column-oriented AlignedData from row-oriented series. */
function buildData(): uPlot.AlignedData {
  // Union of all x values across series, sorted ascending, de-duplicated.
  const xset = new Set<number>();
  for (const s of props.series) for (const [t] of s.points) xset.add(t);
  const xs = [...xset].sort((a, b) => a - b);
  const index = new Map(xs.map((x, i) => [x, i]));

  const cols: uPlot.AlignedData = [
    props.xIsTime ? xs.map((x) => x / 1000) : xs, // uPlot time scale wants seconds
  ];
  for (const s of props.series) {
    const col = new Array<number | null>(xs.length).fill(null);
    for (const [t, v] of s.points) {
      const i = index.get(t);
      if (i !== undefined) col[i] = v;
    }
    cols.push(col);
  }
  return cols;
}

function pathBuilder(kind: VizKind) {
  const p = (uPlot as unknown as { paths: Record<string, (o?: unknown) => unknown> }).paths;
  if (kind === 'bar') return p.bars?.({ size: [0.85, 100] });
  // line, area and points all use the linear path (points also shows markers,
  // area adds a fill — both handled on the series opts below).
  return p.linear?.();
}

function seriesOpts(el: HTMLElement): uPlot.Series[] {
  const palette = resolveVizPalette(el);
  return props.series.map((s) => {
    const kind = s.kind ?? props.type;
    const color = s.color || colorForName(s.name, palette);
    const opts: uPlot.Series = {
      label: s.name,
      stroke: color,
      width: kind === 'points' ? 0 : 2,
      spanGaps: false,
      value: (_u, v) => (v == null ? '—' : formatMetric(v, props.yUnit)),
      paths: pathBuilder(kind) as uPlot.Series.PathBuilder,
    };
    if (kind === 'area' || kind === 'bar') {
      opts.fill = withAlpha(color, kind === 'bar' ? 0.55 : 0.15);
    }
    if (kind === 'points') {
      opts.points = { show: true, size: 5, stroke: color, fill: color };
    } else {
      opts.points = { show: false };
    }
    return opts;
  });
}

/** Append an alpha channel to a resolved hex/rgb color for fills. */
function withAlpha(color: string, alpha: number): string {
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

function buildOptions(el: HTMLElement, width: number): uPlot.Options {
  const axisColor = resolveCssVar(el, '--lg-axis') || '#99a3af';
  const gridColor = resolveCssVar(el, '--lg-grid') || '#21262d';
  const font = `11px ${resolveCssVar(el, '--lg-font-sans') || 'system-ui, sans-serif'}`;

  const axisBase = {
    stroke: axisColor,
    font,
    grid: { stroke: gridColor, width: 1 },
    ticks: { stroke: gridColor, width: 1, size: 4 },
  };

  return {
    width,
    height: props.height,
    ms: 1, // our x is already unix seconds after /1000; keep splits sane
    padding: [8, 8, 0, 0],
    cursor: { focus: { prox: 24 } },
    legend: { live: true },
    scales: {
      x: { time: props.xIsTime },
      y: { distr: props.yScale === 'log' ? 3 : 1 },
    },
    axes: [
      {
        ...axisBase,
        values: props.xIsTime
          ? (_u, splits) =>
              splits.map((s) => formatClockTime(s * 1000, { seconds: xShowSeconds(splits) }))
          : undefined,
      },
      {
        ...axisBase,
        size: 52,
        values: (_u, splits) => splits.map((v) => (v == null ? '' : formatMetric(v, props.yUnit))),
      },
    ],
    series: [
      { label: props.xIsTime ? 'time' : 'x', value: (_u, v) => xValueLabel(v) },
      ...seriesOpts(el),
    ],
  };
}

/** Show seconds on x ticks only when the visible window is short (< ~10 min). */
function xShowSeconds(splitsSecs: number[]): boolean {
  if (splitsSecs.length < 2) return false;
  const spanSecs = splitsSecs[splitsSecs.length - 1] - splitsSecs[0];
  return spanSecs < 600;
}

function xValueLabel(v: number | null): string {
  if (v == null) return '—';
  return props.xIsTime ? formatClockTime(v * 1000, { seconds: true }) : String(v);
}

function measureWidth(): number {
  return host.value?.clientWidth || host.value?.parentElement?.clientWidth || 320;
}

function destroyChart() {
  chart?.destroy();
  chart = null;
}

function initChart() {
  destroyChart();
  renderError.value = null;
  if (!host.value || !hasData.value) return;
  try {
    const opts = buildOptions(host.value, measureWidth());
    chart = new uPlot(opts, buildData(), host.value);
  } catch (err) {
    // jsdom (no canvas) and malformed data land here — surface, don't crash.
    renderError.value = 'Chart could not be rendered.';
    console.warn('[LgChart] render failed:', err);
    chart = null;
  }
}

function refresh() {
  // In-place data update when live and the structure is unchanged.
  if (props.live && chart) {
    try {
      chart.setData(buildData());
      return;
    } catch {
      // fall through to a full rebuild
    }
  }
  initChart();
}

onMounted(() => {
  initChart();
  // ResizeObserver is absent in jsdom (tests) — guard so mounting never throws.
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (chart) chart.setSize({ width: measureWidth(), height: props.height });
      });
    });
    if (host.value) ro.observe(host.value);
  }
});

onBeforeUnmount(() => {
  cancelAnimationFrame(rafId);
  ro?.disconnect();
  ro = null;
  destroyChart();
});

// Structural changes → rebuild; pure data changes → cheap refresh (if live).
watch(structure, () => nextTick(initChart));
watch(seriesRevision, () => nextTick(refresh));
watch(
  () => props.height,
  (h) => chart?.setSize({ width: measureWidth(), height: h }),
);
// Rebuild when the empty→populated boundary is crossed (host is v-show'd).
watch(hasData, () => nextTick(initChart));
</script>

<style scoped>
.lg-chart {
  position: relative;
  width: 100%;
  min-width: 0;
  background: var(--lg-bg-raised);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  overflow: hidden;
}
.lg-chart-host {
  width: 100%;
  height: 100%;
}
.lg-chart-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
  text-align: center;
  padding: var(--lg-space-4);
}
.lg-chart-state.error {
  color: var(--lg-danger);
}
.lg-chart-spin {
  animation: lg-chart-spin 0.9s linear infinite;
}
@keyframes lg-chart-spin {
  to {
    transform: rotate(360deg);
  }
}

/* uPlot chrome — recolor the bundled defaults to the dark token palette.
   These elements are created by uPlot inside our root, so :deep reaches them. */
.lg-chart :deep(.u-legend) {
  color: var(--lg-text-dim);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-xs);
  padding: var(--lg-space-1) var(--lg-space-2);
}
.lg-chart :deep(.u-legend .u-marker) {
  border-radius: var(--lg-radius-sm);
}
.lg-chart :deep(.u-legend .u-value) {
  color: var(--lg-text);
  font-family: var(--lg-font-mono);
}
.lg-chart :deep(.u-cursor-x),
.lg-chart :deep(.u-cursor-y) {
  border-color: var(--lg-border-strong);
}
.lg-chart :deep(.u-select) {
  background: color-mix(in srgb, var(--lg-accent) 14%, transparent);
}
</style>
